// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPasswordPolicy, updatePasswordPolicy } from '@api/portalServices';
import type { PasswordPolicyUpdateRequest, PasswordPolicyGetResponse } from '@api/types';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';

/**
 * Custom hook to fetch password policy configuration
 */
export const usePasswordPolicyQuery = () => {
  return useQuery<PasswordPolicyGetResponse, ApiError>({
    queryKey: [QueryKey.PASSWORD_POLICY],
    queryFn: getPasswordPolicy as () => Promise<PasswordPolicyGetResponse>,
    retry: false,
    staleTime: 0,
  });
};

/**
 * Custom hook to update password policy configuration
 */
export const usePasswordPolicyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, PasswordPolicyUpdateRequest>({
    mutationFn: async (data: PasswordPolicyUpdateRequest) => {
      await updatePasswordPolicy(data);
    },
    onSuccess: () => {
      // Invalidate and refetch password policy data after successful update
      queryClient.invalidateQueries({ queryKey: [QueryKey.PASSWORD_POLICY] });
    },
    retry: false,
  });
};
